// Small browser helpers shared by every screen.
import type { Cents } from '../types.ts';
import { plain } from '../money.ts';
import { wordmarkHtml } from '../brand.ts';

export const app = document.getElementById('app') as HTMLElement;
const toastEl = document.getElementById('toast') as HTMLElement;

export const esc = (s: unknown): string =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

/** localStorage that never throws (private mode, blocked storage). */
export const ls = {
  get<T>(key: string, fallback: T): T {
    try {
      const v = localStorage.getItem(key);
      return v ? (JSON.parse(v) as T) : fallback;
    } catch {
      return fallback;
    }
  },
  set(key: string, value: unknown): void {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable: fine */ }
  },
};

export const initial = (n: string): string => (String(n || '?').trim()[0] || '?').toUpperCase();
export const toCents = (s: string): Cents => { const n = parseFloat(String(s).replace(/[^0-9.\-]/g, '')); return Number.isFinite(n) ? Math.round(n * 100) : 0; };
export const frac = (n: number): string => ({ 2: '½', 3: '⅓', 4: '¼' } as Record<number, string>)[n] || `1/${n}`;
export const listNames = (names: string[]): string =>
  names.length <= 1 ? names.join('') : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
export const todayLabel = (): string => new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
export const clampSize = (n: number): number => Math.max(2, Math.min(40, n));

/** The element an event came from, typed, or null. */
export function target<T extends HTMLElement = HTMLElement>(e: Event, selector: string): T | null {
  return e.target instanceof Element ? e.target.closest<T>(selector) : null;
}
export const inputValue = (id: string): string => (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? '';

export const icon = {
  apple: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style="vertical-align:-3px"><path d="M16.4 12.6c0-2.4 2-3.5 2-3.6-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.2-2.8.8-3.5.8s-1.8-.8-3-.8C7 7.2 5.5 8.1 4.7 9.6c-1.7 2.9-.4 7.2 1.2 9.6.8 1.2 1.7 2.4 2.9 2.4s1.6-.8 3-.8 1.8.8 3 .8 2-1.2 2.8-2.4c.9-1.3 1.2-2.6 1.3-2.7-.1 0-2.5-.9-2.5-3.9zM14.1 5.6c.6-.8 1.1-1.8 1-2.9-.9 0-2 .6-2.7 1.4-.6.7-1.1 1.8-1 2.8 1 .1 2.1-.5 2.7-1.3z"/></svg>',
  android: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style="vertical-align:-3px"><path d="M6 9h12v8a1 1 0 0 1-1 1h-1v3h-2v-3h-4v3H8v-3H7a1 1 0 0 1-1-1zM4 9h1.5v6H4zm14.5 0H20v6h-1.5zM7 8a5 5 0 0 1 10 0zm3-2.5a.7.7 0 1 0 0 .01zm4 0a.7.7 0 1 0 0 .01z"/></svg>',
  plusSquare: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-label="Add" style="vertical-align:-3px"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M12 8v8M8 12h8"/></svg>',
  dots: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-label="Menu" style="vertical-align:-3px"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>',
  share: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="Share" style="vertical-align:-3px"><path d="M12 3v12M8 7l4-4 4 4"/><path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1"/></svg>',
  back: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>',
  check: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>',
  bigCheck: '<svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>',
  camera: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3v11H4z"/><circle cx="12" cy="13" r="3.5"/></svg>',
  x: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
  info: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true" style="flex:none"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5M12 16.5v.5"/></svg>',
};

/** The logo in the top bar, from src/brand.ts. */
export const logo = `<a class="brand" href="/" data-go>${wordmarkHtml()}</a>`;
/** Next to every place a phone number is asked for. Opens in a new tab so nothing typed is lost. */
export const privacyLink = (text = 'Privacy') => `<a href="/privacy" target="_blank" rel="noopener">${text}</a>`;
export const lead = (label: string, value: string, cls = ''): string => `<div class="lead ${cls}"><span>${label}</span><i></i><span>${value}</span></div>`;
export const leadCents = (label: string, cents: Cents, cls = ''): string => lead(label, plain(cents), cls);
export const stepper = (n: number, min = 2): string => `
  <div class="stepper">
    <button type="button" class="icon-btn" data-act="size" data-d="-1" aria-label="One fewer person" ${n <= min ? 'disabled' : ''}>−</button>
    <span class="amt" aria-live="polite">${n}</span>
    <button type="button" class="icon-btn" data-act="size" data-d="1" aria-label="One more person">+</button>
  </div>`;

/** The payer's steps, start to finish. Splitting evenly skips tapping your own items. */
export const flowSteps = (even: boolean): string[] => ['Bill', 'People', 'Tip', 'Share', ...(even ? [] : ['Your items']), 'Get paid'];

/** The top bar on each payer step: where to go back to, "Step 2 of 6", and a bar that fills up. */
export function stepBar(left: string, even: boolean, at: string): string {
  const steps = flowSteps(even);
  const i = Math.max(0, steps.indexOf(at));
  return `
    <div class="bar">${left}<span>Step ${i + 1} of ${steps.length}</span></div>
    <div class="flow" role="progressbar" aria-valuemin="1" aria-valuemax="${steps.length}" aria-valuenow="${i + 1}" aria-label="Step ${i + 1} of ${steps.length}: ${at}">
      ${steps.map((_, k) => `<span${k <= i ? ' class="on"' : ''}></span>`).join('')}
    </div>`;
}

/** Opens Messages with the text filled in. No number: they pick people, or a group chat, in Messages. */
export const smsHref = (to: string, text: string): string => `sms:${encodeURIComponent(to)}?&body=${encodeURIComponent(text)}`;

let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function toast(msg: string): void {
  toastEl.textContent = msg;
  toastEl.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toastEl.hidden = true; }, 2800);
}

export async function copy(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied');
  } catch {
    const t = document.createElement('textarea');
    t.value = text;
    document.body.appendChild(t);
    t.select();
    try { document.execCommand('copy'); toast('Copied'); } catch { toast('Copy failed. Press and hold to copy instead.'); }
    t.remove();
  }
}

// ---------- re-rendering without wiping what someone is typing ----------

const isField = (el: Element | null): boolean => !!el && el.matches('input, textarea, select');
let pendingRender: (() => void) | null = null;

export function safeRender(fn: () => void): void {
  if (isField(document.activeElement)) { pendingRender = fn; return; }
  pendingRender = null;
  keepTyped(fn);
}

/**
 * Redraw, then put back anything typed into boxes that are still there. A box can be filled but
 * not focused: iPhone AutoFill takes focus away to show the contact card, then fills the box.
 */
function keepTyped(fn: () => void): void {
  const typed = [...app.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input[id], textarea[id]')]
    .filter((el) => !['file', 'checkbox', 'radio', 'hidden'].includes(el.type) && el.value)
    .map((el) => [el.id, el.value] as const);
  fn();
  for (const [id, value] of typed) {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el && app.contains(el) && !el.value) el.value = value;
  }
}
export function cancelPendingRender(): void { pendingRender = null; }
function flushPending(): void {
  if (pendingRender && !isField(document.activeElement)) { const f = pendingRender; pendingRender = null; keepTyped(f); }
}
document.addEventListener('focusout', () => setTimeout(flushPending, 0));

// ---------- bottom sheets ----------

let sheetEl: HTMLElement | null = null;

/** The open sheet's content box, or null when no sheet is open. */
export const sheetBody = (): HTMLElement | null => sheetEl?.querySelector<HTMLElement>('.sheet') ?? null;

export function openSheet(html: string, onClick?: (e: MouseEvent) => void, onInput?: (e: Event) => void): HTMLElement {
  closeSheet();
  const wrap = document.createElement('div');
  wrap.className = 'sheet-wrap';
  wrap.innerHTML = `<div class="sheet" role="dialog" aria-modal="true"><div class="grab"></div>${html}</div>`;
  wrap.addEventListener('click', (e) => {
    if (e.target === wrap || target(e, '[data-close]')) return closeSheet();
    onClick?.(e);
  });
  if (onInput) wrap.addEventListener('input', onInput);
  document.body.appendChild(wrap);
  sheetEl = wrap;
  wrap.querySelector<HTMLElement>('button, a, input, textarea')?.focus({ preventScroll: true });
  return wrap.querySelector('.sheet') as HTMLElement;
}

export function closeSheet(): void {
  sheetEl?.remove();
  sheetEl = null;
  flushPending();
}
